import type { AtBackOfProp } from "building-types/electrical/electricalLayoutTypes";
import { ElectricalLayoutType } from "building-types/electrical/electricalLayoutTypesEnum";
import { LoadTypeSlot } from "building-types/electrical/electricalSlots";
import {
  ControllerSelectionType,
  ControllerSwitchType,
  ControllerType,
} from "building-types/electrical/electricalTypesEnum";
import type { Controller, GeneratedElectricalGroup, Load } from "building-types/electrical/generatedElectricalTypes";
import { getLoadCatalog } from "doc/getLoadCatalog";
import { makeBuiltInLabelTable } from "doc/utils/labelTools";
import { aabb2ByPoints, aabb2Center } from "geom/aabb2";
import type { Vec2 } from "geom/vec2";
import { vec2Copy, vec2Dist } from "geom/vec2";
import { commitControllerSelection } from "../controllerSelection/controllerSelectionCommit";
import type { ElectricalLayoutFunc, ElectricalLayoutOpts } from "../electricalLayoutTypes";
import { loadTypeFromSlots } from "../loadTypeFromSlots";
import { pointOffsetFromBackOfProp } from "../stages/electricalIndependentLoadsFromRoom";

export function atBackOfProp({
  controllerPlacementType = [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
  loadSlot = LoadTypeSlot.PRIMARY_LIGHT,
  loadType,
  placements,
  propType: propId,
  connected = true,
}: AtBackOfProp): ElectricalLayoutFunc {
  return async (opts: ElectricalLayoutOpts) => {
    const {
      room,
      controllerSelectionFunc,
      elecSlots,
      roomProps,
      propCatalog,
      mergedPropLibrary,
      electricalItemCatalogSettings,
      mirror,
    } = opts;
    const resolvedLoadType = loadType ?? loadTypeFromSlots(loadSlot, elecSlots);
    const loadCatalog = getLoadCatalog(electricalItemCatalogSettings);
    const loadDescriptor = loadCatalog[resolvedLoadType];
    const { outline } = room;

    const lightLocs: Vec2[] = [];
    const loads: Load[] = [];

    for (const placement of placements) {
      await pointOffsetFromBackOfProp(
        placement,
        roomProps,
        propId,
        propCatalog,
        mergedPropLibrary,
        loads,
        resolvedLoadType,
        loadDescriptor,
        room.level.id,
        room,
        outline,
        mirror,
      );
    }

    for (const load of loads) {
      lightLocs.push(vec2Copy(load.position));
    }

    if (lightLocs.length === 0) {
      return [];
    }

    const lightsBB = aabb2ByPoints(lightLocs);
    const lightCenter = aabb2Center(lightsBB);
    const candidate = await controllerSelectionFunc(controllerPlacementType, lightCenter);
    const controllerPosition = commitControllerSelection(
      candidate,
      ElectricalLayoutType.AT_BACK_OF_PROP,
      ControllerType.SWITCH,
      lightCenter,
    );

    const updatedLoads: Load[] = [];
    for (const load of loads) {
      updatedLoads.push({ ...load, hideConnections: !connected });
    }

    if (!controllerPosition) {
      const lightElectricalGroup: GeneratedElectricalGroup = {
        levelId: opts.room.level.id,
        loads: updatedLoads,
        controllers: [],
      };

      return [lightElectricalGroup];
    }

    const { position: cLocation } = controllerPosition;
    const [cLocX, cLocY] = cLocation;

    loads.sort((a, b) => {
      const [aX, aY] = a.position;
      const [bX, bY] = b.position;
      return vec2Dist(aX, aY, cLocX, cLocY) - vec2Dist(bX, bY, cLocX, cLocY);
    });

    const controllers: Controller[] = connected
      ? [
          {
            type: ControllerType.SWITCH,
            switchType: ControllerSwitchType.STANDARD,
            position: controllerPosition,
            loadIndex: 0,
            levelId: room.level.id,
            sourceRoomHandle: room.roomHandle,
            candidateId: candidate?.candidate?.id,
            labels: makeBuiltInLabelTable("addToElectricalPlan"),
          },
        ]
      : [];

    const electricalGroups: GeneratedElectricalGroup[] = [];
    const lightElectricalGroup: GeneratedElectricalGroup = {
      levelId: opts.room.level.id,
      loads: updatedLoads,
      controllers: controllers,
    };

    electricalGroups.push(lightElectricalGroup);

    return electricalGroups;
  };
}
